import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, Landmark, Users, Settings, LogOut, Menu, FileInput, BarChart3, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { BrandLockup } from "@/components/Brand";
import { PreviewPicker } from "@/components/PreviewPicker";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/loans", label: "Loans", icon: Landmark, id: "loans" },
  { to: "/admin/applications", label: "Applications", icon: FileInput, id: "applications" },
  { to: "/admin/borrowers", label: "Borrowers", icon: Users, id: "borrowers" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, id: "reports" },
  { to: "/admin/team", label: "Team", icon: ShieldCheck, id: "team", superOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

const SidebarBody = ({ onNavigate }) => {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [newCount, setNewCount] = useState(0);
  useEffect(() => { api.get("/applications/stats").then((r) => setNewCount(r.data.submitted)).catch(() => {}); }, [pathname]);
  return (
    <div className="sidebar-surface grain relative flex h-full flex-col">
      <div className="relative px-6 pb-6 pt-7">
        <BrandLockup />
        <div className="mt-5"><PreviewPicker onNavigate={onNavigate} /></div>
      </div>
      <div className="mx-6 h-px bg-[hsl(var(--sidebar-border))]" />
      <nav className="relative flex-1 space-y-1 px-3 py-6">
        {NAV.filter((n) => !n.superOnly || user?.role === "super_admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.id}`}
            className={({ isActive }) => `sidebar-link flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium ${isActive ? "active" : ""}`}
          >
            <n.icon className="h-4 w-4" strokeWidth={1.75} />
            {n.label}
            {n.id === "applications" && newCount > 0 && (
              <span className="ml-auto rounded-full bg-[hsl(var(--gold))] px-2 py-0.5 text-[10px] font-bold text-[hsl(var(--accent-foreground))]" data-testid="applications-new-badge">{newCount} new</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="relative border-t border-[hsl(var(--sidebar-border))] px-6 py-5">
        <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name}</div>
        <div className="sidebar-muted truncate text-xs">{user?.email}</div>
        <div className="sidebar-muted mt-0.5 text-[10px] font-semibold uppercase tracking-[0.15em]" data-testid="current-user-role">{user?.role === "super_admin" ? "Super admin" : "Admin"}</div>
        <button
          data-testid="logout-button"
          onClick={() => { logout(); navigate("/login"); }}
          className="sidebar-link mt-4 inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-[0.15em]"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r lg:block">
        <SidebarBody />
      </aside>
      <header className="sidebar-surface sticky top-0 z-20 flex items-center justify-between border-b px-4 py-3 lg:hidden">
        <BrandLockup compact />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="rounded-md p-2" data-testid="mobile-nav-toggle" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 border-0 p-0">
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
        <Outlet />
      </main>
    </div>
  );
}
